import React from "react";
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Button,
  Typography,
  Chip,
  Link,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import Paper from "@mui/material/Paper";
import {
  SCHOOL_TYPES,
  SCHOOL_TYPE_COLORS,
  RISK_LEVELS,
  getRiskLevelColor,
} from "./vaccinationUtils";
import "./SearchAndFilterBar.css";

interface SearchAndFilterBarProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  schoolTypeFilter: string;
  setSchoolTypeFilter: (value: string) => void;
  riskLevelFilter: string;
  setRiskLevelFilter: (value: string) => void;
  filteredSchools: any[];
  clearAllFilters: () => void;
}

const SearchAndFilterBar: React.FC<SearchAndFilterBarProps> = ({
  searchTerm,
  setSearchTerm,
  schoolTypeFilter,
  setSchoolTypeFilter,
  riskLevelFilter,
  setRiskLevelFilter,
  filteredSchools,
  clearAllFilters,
}) => {
  const handleSchoolTypeChange = (event: SelectChangeEvent) => {
    setSchoolTypeFilter(event.target.value);
  };

  const handleRiskLevelChange = (event: SelectChangeEvent) => {
    setRiskLevelFilter(event.target.value);
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }} className="sticky-filter-container">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.875,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        {/* Search Bar */}
        <Box sx={{ minWidth: { xs: "30%", md: "45%" } }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <SearchIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              Search by:
            </Typography>
          </Box>
          <TextField
            variant="outlined"
            placeholder="School or district..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
          />
        </Box>

        {/* School Type Filter */}
        <Box sx={{ minWidth: { xs: "26%", md: "23%" } }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <FilterListIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1,
                marginBottom: 0,
              }}
            >
              School Type:
            </Typography>
          </Box>
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <Select
              value={schoolTypeFilter}
              onChange={handleSchoolTypeChange}
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  paddingRight: "32px",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {SCHOOL_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type !== "All" ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        sx={{
                          width: 16,
                          height: 16,
                          padding: 0,
                          backgroundColor:
                            SCHOOL_TYPE_COLORS[
                              type as keyof typeof SCHOOL_TYPE_COLORS
                            ],
                          "& .MuiChip-label": {
                            display: "none",
                          },
                        }}
                      />
                      {type}
                    </Box>
                  ) : (
                    type
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Risk Level Filter */}
        <Box sx={{ minWidth: { xs: "30%", md: "28%" } }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <HealthAndSafetyIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              Vaccination Rate:
            </Typography>
          </Box>
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "40px",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <Select
              value={riskLevelFilter}
              onChange={handleRiskLevelChange}
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  paddingRight: "32px",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {RISK_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level !== "All" ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        sx={{
                          width: 16,
                          height: 16,
                          padding: 0,
                          backgroundColor: getRiskLevelColor(level),
                          "& .MuiChip-label": {
                            display: "none",
                          },
                        }}
                      />
                      {level}
                    </Box>
                  ) : (
                    level
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Stack
        width={"100%"}
        flexDirection={{ xs: "column", sm: "row" }}
        className="filter-actions"
        direction="column"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ flexWrap: "wrap", mt: 2 }}
      >
        {/* Active Filters Display */}
        {(schoolTypeFilter !== "All" ||
          riskLevelFilter !== "All" ||
          searchTerm) && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Active filters:
              </Typography>

              {schoolTypeFilter !== "All" && (
                <Chip
                  label={`Type: ${schoolTypeFilter}`}
                  size="medium"
                  onDelete={() => setSchoolTypeFilter("All")}
                  color="primary"
                  variant="outlined"
                  sx={{
                    backgroundColor:
                      SCHOOL_TYPE_COLORS[
                        schoolTypeFilter as keyof typeof SCHOOL_TYPE_COLORS
                      ],
                    color: "white",
                    borderColor:
                      SCHOOL_TYPE_COLORS[
                        schoolTypeFilter as keyof typeof SCHOOL_TYPE_COLORS
                      ],
                    "& .MuiChip-deleteIcon": {
                      color: "white",
                    },
                  }}
                />
              )}

              {riskLevelFilter !== "All" && (
                <Chip
                  label={`${riskLevelFilter}`}
                  size="medium"
                  onDelete={() => setRiskLevelFilter("All")}
                  color="primary"
                  variant="outlined"
                  sx={{
                    backgroundColor: getRiskLevelColor(riskLevelFilter),
                    color: riskLevelFilter.includes("Moderate")
                      ? "rgba(0, 0, 0, 0.87)"
                      : "white",
                    borderColor: getRiskLevelColor(riskLevelFilter),
                    "& .MuiChip-deleteIcon": {
                      color: riskLevelFilter.includes("Moderate")
                        ? "rgba(0, 0, 0, 1)"
                        : "white",
                    },
                  }}
                />
              )}

              <Link
                component="button"
                variant="body2"
                onClick={clearAllFilters}
                sx={{
                  textDecoration: "none",
                  color: "primary.main",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                clear all
              </Link>
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default SearchAndFilterBar;
